import {
  Alert,
  Button,
  LinearProgress,
  LinearProgressProps,
} from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { messagingActions } from '../../reducers/messagingSlice';
import { CartItem } from '../../utils/cart';
import { getOrderErrorMessage } from '../../utils/errors';
import {
  LoyaltyResponseStatus,
  OrderStatus,
} from '../../reducers/orderSlice.constants';
import {
  selectMenuModuleCartErrors,
  selectOrderError,
} from '../../selectors/order';
import { IEntityCartItem } from '../../reducers/cartSlice.props';
import { selectFeatureFlagLoyaltySupport } from '../../redux/features/config/config.selector';
import { cartErrorMatchesItem } from '../../utils/menuModuleError';

interface IOrderProgressBar {
  status?: OrderStatus;
  cartItem?: CartItem | IEntityCartItem;
}

export default function OrderProgressBar({
  status,
  cartItem,
}: IOrderProgressBar) {
  const dispatch = useDispatch();
  const orderError = useSelector(selectOrderError);
  const menuModuleCartErrors = useSelector(selectMenuModuleCartErrors);
  const FEATURE_FLAG_LOYALTY_SUPPORT = useSelector(
    selectFeatureFlagLoyaltySupport
  );

  // Reason-specific error resolved to this exact cart line. HTTP-path errors
  // carry a cartItemId; AI hypothesis errors match by top-level node name.
  const cartItemId =
    cartItem && 'cartItemId' in cartItem ? cartItem.cartItemId : undefined;
  const cartItemName = cartItem?.name;
  const cartError = menuModuleCartErrors.find((e) =>
    cartErrorMatchesItem(e, cartItemId ?? -1, cartItemName)
  );

  let progress = 0;
  let color: LinearProgressProps['color'] = undefined;
  switch (status) {
    case OrderStatus.sending:
      color = undefined;
      progress = 33;
      break;
    case OrderStatus.pending:
      color = undefined;
      progress = 66;
      break;
    case OrderStatus.completed:
      color = 'success';
      progress = 100;
      break;
    case OrderStatus.failed:
      color = 'error';
      progress = 100;
      break;
    default:
      color = undefined;
      progress = 0;
  }

  const sendMessage = (message: string) => {
    const payload = {
      data: { payload: message, status: 'final' },
    };
    dispatch(messagingActions.sendMessage(payload as any));
  };

  // Render the error alert when this line failed to submit (HTTP path sets
  // OrderStatus.failed) or when a menu-module error resolves to it (AI
  // hypothesis path, which doesn't set a per-item status).
  if (
    (status === OrderStatus.failed || cartError) &&
    cartItem &&
    orderError !== LoyaltyResponseStatus.invalid
  ) {
    const errorMessage =
      cartError?.message || orderError || 'An Unkown Error Has Occurred';
    const messageToCustomer = getOrderErrorMessage(cartItem);
    return (
      <Button
        onClick={() => sendMessage(messageToCustomer)}
        sx={{
          display: 'block',
          padding: '0px',
          width: '100%',
        }}
      >
        <Alert
          variant="filled"
          severity="error"
          sx={{
            '&:hover': {
              backgroundColor: '#FF3D74',
            },
          }}
        >
          {errorMessage}
        </Alert>
      </Button>
    );
  }

  return (
    <LinearProgress variant="determinate" color={color} value={progress} />
  );
}
